import React, { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import axios from 'axios';
import { X, Camera, Type, Check, AlertCircle } from 'lucide-react';
import { BACKEND_URL } from '@/config';

const API = BACKEND_URL + '/api';

/**
 * QrScanModal — le transporteur scanne un code qu'on lui montre (T12).
 *
 * step="pickup"   → le code de prise en charge, affiché par l'expéditeur à la remise du colis
 * step="delivery" → le code de livraison, affiché par le destinataire (qui l'a reçu de l'expéditeur)
 *
 * Le transporteur ne reçoit jamais les codes du serveur : il doit les scanner (caméra) ou les saisir. Avant, l'écran
 * chargeait le code de livraison et le transporteur confirmait la livraison seul, en un clic.
 */
export default function QrScanModal({ onClose, onSuccess, step = 'pickup' }) {
  const [mode, setMode] = useState('camera');
  const [manualValue, setManualValue] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const scannerRef = useRef(null);
  const startedRef = useRef(false);

  const extractToken = (value) => {
    if (!value) return null;
    const v = value.trim();
    if (v.includes('/verify/')) {
      return v.split('/verify/').pop().split('?')[0].split('#')[0];
    }
    return v;
  };

  const submitToken = async (rawToken) => {
    const token = extractToken(rawToken);
    if (!token || submitting) return;
    setSubmitting(true);
    setResult(null);
    try {
      const endpoint = step === 'delivery' ? '/qr/verify-delivery' : '/qr/verify-pickup';
      const { data } = await axios.post(`${API}${endpoint}`, { token });
      const msg = data.status === 'already_verified'
        ? 'Déjà validé ✓'
        : step === 'delivery'
          ? 'Livraison confirmée ! 🎉'
          : 'Prise en charge enregistrée ! 🚚';
      setResult({ ok: true, message: msg });
      setTimeout(() => { onSuccess?.(data); onClose(); }, 1500);
    } catch (err) {
      setResult({ ok: false, message: err?.response?.data?.detail || 'Erreur de vérification.' });
      setSubmitting(false);
    }
  };

  // Lifecycle caméra
  useEffect(() => {
    if (mode !== 'camera' || startedRef.current) return;
    let active = true;
    const id = 'qr-scan-region';
    const start = async () => {
      try {
        const html5Qrcode = new Html5Qrcode(id, false);
        scannerRef.current = html5Qrcode;
        startedRef.current = true;
        await html5Qrcode.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: 240 },
          (decoded) => {
            if (!active) return;
            const token = extractToken(decoded);
            if (token) {
              html5Qrcode.stop().catch(() => {});
              submitToken(token);
            }
          },
          () => {},
        );
      } catch {
        setMode('manual');
      }
    };
    start();
    return () => {
      active = false;
      if (scannerRef.current && startedRef.current) {
        try { scannerRef.current.stop().then(() => scannerRef.current.clear()).catch(() => {}); } catch {}
      }
      startedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const title    = step === 'delivery' ? 'Livraison du colis' : 'Prise en charge du colis';
  const subtitle = step === 'delivery'
    ? 'Scannez le QR présenté par le destinataire'
    : "Scannez le QR présenté par l'expéditeur";

  return (
    <div className="qr-modal-bg" data-testid="qr-scan-modal" onClick={onClose}>
      <div className="qr-modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>

        <div className="qr-modal-header">
          <div>
            <div className="qr-modal-title">{title}</div>
            <div className="qr-modal-sub">{subtitle}</div>
          </div>
          <button className="qr-modal-close" onClick={onClose} aria-label="Fermer"><X size={18} /></button>
        </div>

        <div className="qr-modal-body">
          {result ? (
            <div className={`qr-scan-result ${result.ok ? 'ok' : 'err'}`} data-testid="qr-scan-result">
              {result.ok ? <Check size={36} /> : <AlertCircle size={36} />}
              <div className="qr-scan-result-msg">{result.message}</div>
            </div>
          ) : (
            <>
              {/* Tabs */}
              <div className="qr-scan-tabs">
                <button className={`qr-scan-tab ${mode === 'camera' ? 'active' : ''}`}
                  onClick={() => setMode('camera')}>
                  <Camera size={14} /> Caméra
                </button>
                <button className={`qr-scan-tab ${mode === 'manual' ? 'active' : ''}`}
                  onClick={() => setMode('manual')}>
                  <Type size={14} /> Saisie manuelle
                </button>
              </div>

              {/* Caméra */}
              {mode === 'camera' && (
                <div className="qr-scan-camera">
                  <div id="qr-scan-region" data-testid="qr-scan-region"
                    style={{ width: '100%', borderRadius: 14, overflow: 'hidden', background: '#000' }} />
                  <div className="qr-scan-hint">Place le QR dans le cadre. Il sera lu automatiquement.</div>
                </div>
              )}

              {/* Saisie manuelle */}
              {mode === 'manual' && (
                <form onSubmit={e => { e.preventDefault(); submitToken(manualValue); }}>
                  <label className="qr-scan-label">Colle l'URL ou le token QR</label>
                  <input
                    autoFocus
                    type="text"
                    value={manualValue}
                    onChange={e => setManualValue(e.target.value)}
                    placeholder="https://app/verify/... ou token brut"
                    data-testid="qr-manual-input"
                    className="qr-scan-input"
                  />
                  <button className="qr-btn-primary" type="submit"
                    disabled={submitting || !manualValue.trim()}
                    data-testid="qr-manual-submit">
                    {submitting ? 'Vérification...' : 'Valider'}
                  </button>
                </form>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
